import { ChordLib, getCadenceOptions, getCadencePrefix } from '@tunasong/music-lib'
import type { Chord } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import { CadenceOptionCard } from './cadence-option-card.js'
import styles from './cadence-lab.module.css'

const MIN_CHORDS = 3

/**
 * Bowie's §8 Q6 — footer caption shown only when the scale is modal-no-leading-tone.
 */
const MODAL_FOOTER =
  'Authentic and deceptive cadences need a leading tone — modes without one (Mixolydian, Dorian, Phrygian, Aeolian, Locrian) work differently.'

const MODES_WITHOUT_LEADING_TONE = new Set(['mixolydian', 'dorian', 'phrygian', 'aeolian', 'locrian'])

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export interface CadenceLabProps {
  scale: Scale | null | undefined
  chords: Chord[]
  open: boolean
  onClose: () => void
}

/**
 * Cadence Lab drawer. Renders nothing when closed, when the scale is unset,
 * or when the section has fewer than 3 chords (the section-trigger button
 * enforces the latter two as `disabled`; this is a defensive guard at the
 * host level).
 *
 * Spec: docs/superpowers/specs/2026-05-17-cadence-lab-design.md
 */
export const CadenceLab: FC<CadenceLabProps> = props => {
  const { scale, chords, open, onClose } = props
  if (!open || !scale || chords.length < MIN_CHORDS) {
    return null
  }
  const prefix = getCadencePrefix(chords)
  const options = getCadenceOptions(scale)
  const scaleType = scale.type ?? 'major'
  const modeName = capitalize(scaleType)
  const isMinorKey = scaleType === 'minor'
  const showModalFooter = MODES_WITHOUT_LEADING_TONE.has(scaleType)

  return (
    <div className={styles.drawer} role="dialog" aria-label="Cadence Lab">
      <div className={styles.header}>
        <h2 className={styles.title}>Cadence Lab</h2>
        <button type="button" aria-label="Close cadence lab" onClick={onClose}>
          ×
        </button>
      </div>
      {showModalFooter ? <p className={styles.modalFooter}>{MODAL_FOOTER}</p> : null}
      <div className={styles.prefixStrip}>Prefix: {prefix.map(c => ChordLib.getName(c)).join(' → ')}</div>
      <div className={styles.grid}>
        {options.map(option => (
          <CadenceOptionCard
            key={option.kind}
            option={option}
            prefix={prefix}
            modeName={modeName}
            isMinorKey={isMinorKey}
          />
        ))}
      </div>
    </div>
  )
}
